/**
 * /coach/customer-journey — Owner-only QA tool.
 *
 * Lets Sachin walk through every step a new customer (or new coach) sees.
 * Each stage row offers one or more "Preview" buttons:
 *
 *   - Public URL  → opens the page in a new tab.
 *   - Sandbox URL → mints a throwaway logged-in user, opens the page in a
 *                   new tab with cookies that auto-sign that user in. The
 *                   sandbox is tagged + tears down after 24h.
 *   - Email       → renders the HTML in an iframe inline.
 *
 * The sandbox session token is delivered via the URL hash so it never lives
 * in the URL bar history; a tiny bootstrap on `/journey/launch` reads the
 * hash, swaps the cookie, then forwards to the real destination.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowLeft, Globe, FileText, Mail, ShieldCheck, UserPlus, Home,
    CreditCard, MessageSquare, Calendar, Dumbbell, Loader2, ExternalLink,
    Trash2, AlertCircle, RefreshCw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAS = [
    { value: "adult", label: "Adult athlete (self)" },
    { value: "parent_one", label: "Parent w/ one kid" },
    { value: "parent_many", label: "Parent w/ multiple kids" },
];

const CUSTOMER_STAGES = [
    {
        id: "marketing",
        icon: Globe,
        title: "Marketing site",
        desc: "What an anonymous visitor sees before they sign up. The first 60 seconds of the funnel.",
        previews: [
            { type: "public", label: "Home", path: "/" },
            { type: "public", label: "Programs", path: "/programs" },
            { type: "public", label: "Coaches", path: "/coaches" },
            { type: "public", label: "Visit / Contact", path: "/visit" },
        ],
    },
    {
        id: "book-assessment",
        icon: FileText,
        title: "Assessment booking form",
        desc: "The intake form a lead fills to book their Initial session.",
        previews: [
            { type: "public", label: "Book assessment", path: "/book" },
        ],
    },
    {
        id: "pre-sign-waiver",
        icon: ShieldCheck,
        title: "Pre-sign waiver (v2.0-WA)",
        desc: "Latest waiver text — booking variant (no media release). Sent in the confirmation email.",
        previews: [
            { type: "booking", label: "Preview /waiver", path: "/waiver" },
        ],
    },
    {
        id: "sign-in",
        icon: Mail,
        title: "Magic-link sign-in (members-only gate)",
        desc: "When a stranger tries to sign in with an email that isn't on file, they get blocked at the gate.",
        previews: [
            { type: "public", label: "Sign-in screen", path: "/login" },
        ],
    },
    {
        id: "profile-wizard",
        icon: UserPlus,
        title: "Profile wizard — first sign-in",
        desc: "The 5-step onboarding: who's training → athletes → goals → waiver → done. Different paths per persona.",
        previews: [
            { type: "sandbox", label: "Open as new member" },
        ],
    },
    {
        id: "dashboard-first",
        icon: Home,
        title: "Dashboard — fresh member (no card, no sub)",
        desc: "First post-onboarding landing. Lots of empty states + nudges to add a card / complete profile.",
        previews: [
            { type: "sandbox", label: "Open dashboard", target: "/dashboard" },
        ],
    },
    {
        id: "billing-card-popup",
        icon: CreditCard,
        title: "Auto card-on-file popup",
        desc: "When a member visits Billing without a card, this dialog auto-opens with the dual setup CTAs.",
        previews: [
            { type: "sandbox", label: "Open billing", target: "/dashboard/billing" },
        ],
    },
    {
        id: "messages-first",
        icon: MessageSquare,
        title: "Messages — first visit (coach picker)",
        desc: "Members with no assigned_coach_id see a picker before they can send their first message.",
        previews: [
            { type: "sandbox", label: "Open messages", target: "/dashboard/messages" },
        ],
    },
    {
        id: "schedule-first",
        icon: Calendar,
        title: "Schedule — first visit",
        desc: "Empty schedule view — what they see before they have any bookings.",
        previews: [
            { type: "sandbox", label: "Open schedule", target: "/dashboard/schedule" },
        ],
    },
    {
        id: "workouts-first",
        icon: Dumbbell,
        title: "Workouts — no programs yet",
        desc: "What a member sees before a coach assigns them a program.",
        previews: [
            { type: "sandbox", label: "Open workouts", target: "/dashboard/workouts" },
        ],
    },
];

const COACH_STAGES = [
    {
        id: "coach-login",
        icon: Mail,
        title: "Coach receives invite email",
        desc: "The /coach/mnp invite flow — new coach gets an invite link emailed to them.",
        previews: [
            { type: "public", label: "Sign-in screen", path: "/login" },
        ],
    },
    {
        id: "coach-dashboard",
        icon: Home,
        title: "Coach dashboard — fresh hire",
        desc: "What a new coach sees on first sign-in. Default permissions: workouts + exercises + messages on; billing/accounting off.",
        previews: [
            { type: "sandbox", persona: "coach", label: "Open as new coach", target: "/coach" },
        ],
    },
    {
        id: "coach-messages",
        icon: MessageSquare,
        title: "Coach inbox — empty",
        desc: "What the inbox looks like before any clients message them.",
        previews: [
            { type: "sandbox", persona: "coach", label: "Open inbox", target: "/coach/messages" },
        ],
    },
    {
        id: "coach-workouts",
        icon: Dumbbell,
        title: "Workout builder — empty",
        desc: "The workout authoring surface with no saved programs yet.",
        previews: [
            { type: "sandbox", persona: "coach", label: "Open builder", target: "/coach/workouts" },
        ],
    },
];

export default function CustomerJourney() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [persona, setPersona] = useState("parent_one");
    const [tab, setTab] = useState("customer");
    const [sandboxes, setSandboxes] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        if (!loading && user && user.role !== "owner") {
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    useEffect(() => {
        if (!user || user.role !== "owner") return;
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/journey/sandboxes`, { withCredentials: true });
                if (alive) setSandboxes(r.data.sandboxes || []);
            } catch { /* silent */ }
        })();
        return () => { alive = false; };
    }, [user, refreshKey]);

    const stages = tab === "customer" ? CUSTOMER_STAGES : COACH_STAGES;

    const openPublic = (path) => {
        const url = `${window.location.origin}${path}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const openBookingPreview = async (path) => {
        setBusy("booking");
        try {
            const r = await axios.post(`${API}/coach/journey/booking`, {}, { withCredentials: true });
            const url = `${window.location.origin}${path}?request_id=${r.data.request_id}`;
            window.open(url, "_blank", "noopener,noreferrer");
            toast.success("Sandbox booking minted", { description: "Auto-deletes in 24h." });
        } catch (e) {
            toast.error("Couldn't mint sandbox", { description: e?.response?.data?.detail });
        } finally {
            setBusy(null);
        }
    };

    const openSandbox = async (target = "/dashboard", personaOverride = null) => {
        const personaToUse = personaOverride || persona;
        setBusy(`sandbox-${target}`);
        try {
            const r = await axios.post(
                `${API}/coach/journey/sandbox`,
                { persona: personaToUse, stage: target },
                { withCredentials: true },
            );
            const { session_token, persona_label } = r.data;
            // Open the launcher, which sets the cookie + redirects to target.
            const launchUrl = `${window.location.origin}/journey/launch#token=${encodeURIComponent(session_token)}&next=${encodeURIComponent(target)}`;
            window.open(launchUrl, "_blank", "noopener,noreferrer");
            toast.success(`Sandbox launched: ${persona_label}`, { description: "Auto-deletes in 24h." });
            setRefreshKey((k) => k + 1);
        } catch (e) {
            toast.error("Couldn't open sandbox", { description: e?.response?.data?.detail });
        } finally {
            setBusy(null);
        }
    };

    const runCleanup = async () => {
        setBusy("cleanup");
        try {
            const r = await axios.post(`${API}/coach/journey/cleanup`, {}, { withCredentials: true });
            toast.success("Cleanup ran", { description: `Deleted: ${JSON.stringify(r.data.deleted)}` });
            setRefreshKey((k) => k + 1);
        } catch (e) {
            toast.error("Cleanup failed", { description: e?.response?.data?.detail });
        } finally {
            setBusy(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }
    if (!user || user.role !== "owner") return null;

    return (
        <main data-testid="customer-journey-page" className="min-h-screen bg-zinc-950 text-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-20">
                <Link
                    to="/coach"
                    className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Coach console
                </Link>
                <h1 className="font-display text-3xl sm:text-4xl tracking-wider">Customer journey</h1>
                <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
                    Walk every step a new customer or new coach sees, end-to-end. Sandbox sessions auto-delete after 24h and never touch your real CRM.
                </p>

                {/* Tab + persona controls */}
                <div className="mt-5 flex flex-wrap items-center gap-3 border-b border-zinc-900 pb-4">
                    <div className="flex gap-1 border border-zinc-800">
                        {["customer", "coach"].map((t) => (
                            <button
                                type="button"
                                key={t}
                                data-testid={`journey-tab-${t}`}
                                onClick={() => setTab(t)}
                                className={`text-[10px] tracking-[0.25em] uppercase px-4 py-2 transition-colors ${
                                    tab === t ? "bg-brand-red text-white" : "text-zinc-300 hover:text-white"
                                }`}
                            >
                                {t === "customer" ? "Customer" : "Coach"}
                            </button>
                        ))}
                    </div>
                    {tab === "customer" && (
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Persona</span>
                            <select
                                data-testid="journey-persona"
                                value={persona}
                                onChange={(e) => setPersona(e.target.value)}
                                className="bg-zinc-900 border border-zinc-800 px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-brand-red"
                            >
                                {PERSONAS.map((p) => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            type="button"
                            data-testid="journey-cleanup-btn"
                            onClick={runCleanup}
                            disabled={busy === "cleanup"}
                            title="Force-delete expired sandboxes now (otherwise runs hourly)"
                            className="text-[10px] tracking-[0.2em] uppercase border border-zinc-800 px-2.5 py-1.5 text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                            {busy === "cleanup" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            Cleanup expired
                        </button>
                    </div>
                </div>

                {/* Timeline */}
                <ol className="mt-6 space-y-3" data-testid="journey-timeline">
                    {stages.map((s, idx) => (
                        <Stage
                            key={s.id}
                            n={idx + 1}
                            stage={s}
                            busy={busy}
                            persona={persona}
                            onPublic={openPublic}
                            onBooking={openBookingPreview}
                            onSandbox={openSandbox}
                        />
                    ))}
                </ol>

                {/* Active sandbox list */}
                <SandboxList sandboxes={sandboxes} onRefresh={() => setRefreshKey((k) => k + 1)} />
            </div>
        </main>
    );
}


function Stage({ n, stage, busy, persona, onPublic, onBooking, onSandbox }) {
    const Icon = stage.icon;
    return (
        <li
            data-testid={`journey-stage-${stage.id}`}
            className="border border-zinc-900 bg-zinc-900/30 rounded-lg p-4 sm:p-5"
        >
            <div className="flex items-start gap-4">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-red/15 border border-brand-red/40 text-brand-red font-mono text-sm shrink-0">
                    {n}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-zinc-300" />
                        <h3 className="text-base font-semibold text-white">{stage.title}</h3>
                    </div>
                    <p className="text-xs text-zinc-400 leading-snug">{stage.desc}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {stage.previews.map((p, i) => {
                            const isBusy = busy === `sandbox-${p.target || ""}` || (p.type === "booking" && busy === "booking");
                            const onClick = () => {
                                if (p.type === "public") onPublic(p.path);
                                else if (p.type === "booking") onBooking(p.path);
                                else onSandbox(p.target || "/dashboard", p.persona || null);
                            };
                            return (
                                <button
                                    type="button"
                                    key={i}
                                    data-testid={`journey-preview-${stage.id}-${i}`}
                                    onClick={onClick}
                                    disabled={isBusy}
                                    className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors disabled:opacity-50 ${
                                        p.type === "sandbox"
                                            ? "border-brand-red bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white"
                                            : "border-zinc-800 text-zinc-200 hover:border-brand-red hover:text-brand-red"
                                    }`}
                                >
                                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>
                    {stage.previews.some((p) => p.type === "sandbox") && (
                        <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
                            Persona: <span className="text-zinc-300">{persona === "adult" ? "Adult" : persona === "parent_one" ? "Parent w/ one kid" : "Parent w/ multiple kids"}</span>. Sandbox tears down in 24h.
                        </p>
                    )}
                </div>
            </div>
        </li>
    );
}


function SandboxList({ sandboxes, onRefresh }) {
    if (!sandboxes.length) return null;
    return (
        <section data-testid="journey-active-sandboxes" className="mt-10 border border-zinc-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 inline-flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-400" /> Active sandboxes ({sandboxes.length})
                </h4>
                <button
                    type="button"
                    onClick={onRefresh}
                    className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white inline-flex items-center gap-1"
                >
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>
            <ul className="space-y-1.5">
                {sandboxes.map((s) => (
                    <li key={s.user_id} className="text-xs text-zinc-300 flex items-center justify-between gap-3 font-mono">
                        <span className="truncate">{s.name}</span>
                        <span className="text-[10px] text-zinc-500 shrink-0">expires {new Date(s.expires_at).toLocaleString()}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}
